import React, {useEffect, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import {useHistory} from 'react-router-dom';
import Styled from 'styled-components';
import {Button, EMPHASIS, FlexColumn, FlexRow, SubHeading} from '@forecast-it/design-system';
import {RelayOnError} from '../../../../../mutations/MutationTypes';
import {useRouterPrompt} from '../../../../shared/hooks/useRouterPrompt';
import {BASELINE_TEMPLATE_NAME_TAKEN} from '../../../../shared/util/util';
import {BASELINE_TEMPLATES_PATH} from '../../templatesPaths';
import {closeDiscardTemplateChangesModal, showDiscardTemplateChangesModal} from './DiscardTemplateChangesModal';
import {showDisableTemplateDeliverablesModal} from './DisableTemplateDeliverablesModal';
import {TemplateDetailsCard} from './TemplateDetailsCard';
import {PhaseEditor} from './PhaseEditor';
import {TemplateSummaryPanel} from './TemplateSummaryPanel';
import {
	addPhase,
	BaselineTemplateRequest,
	FieldErrors,
	hasDeliverableContent,
	isDirty,
	isNameTaken,
	normalise,
	Option,
	PhaseDraft,
	removePhase,
	setIncludeDeliverables,
	TemplateDraft,
	untouchedRowKeys,
	updatePhase,
	validate,
} from './baselineTemplateDraft';

const Editor = Styled(FlexColumn)`
	flex: 1;
	min-width: 0;
`;

const NAME_TAKEN_ERROR = 'baseline_templates.errors.name_taken';

/**
 * Drops the errors of phases and estimate rows the draft no longer has. A row's error path starts with the
 * row's key and a dot; errors on the template's own fields have no such prefix. Returns `errors` itself when
 * there is nothing to drop.
 */
export const withoutRemovedRowErrors = (errors: FieldErrors, draft: TemplateDraft): FieldErrors => {
	const live = new Set(
		draft.phases.flatMap(phase => [
			phase.key,
			...phase.timeEstimates.map(row => row.key),
			...phase.expenseEstimates.map(row => row.key),
		])
	);
	const removed = Object.keys(errors).filter(path => path.includes('.') && !live.has(path.slice(0, path.indexOf('.'))));
	if (removed.length === 0) {
		return errors;
	}
	const rest = {...errors};
	removed.forEach(path => delete rest[path]);
	return rest;
};

const scrollToField = (path: string) => {
	document.querySelector(`[data-field-path="${path}"]`)?.scrollIntoView({behavior: 'smooth', block: 'center'});
};

/** Sends a validated, normalised template. The page decides which mutation, and whether it may be retried. */
export type CommitTemplate = (request: BaselineTemplateRequest, onSuccess: () => void, onError: RelayOnError) => void;

type Props = {
	roles: Option[];
	categories: Option[];
	currency: string;
	existingNames: readonly string[];
	/**
	 * A blank template to create one, or the stored template to edit it; unsaved changes are measured from it.
	 * Read once; must be stable for the form's lifetime, since the held options are keyed by its rows.
	 */
	initialDraft: TemplateDraft;
	/** Editing a saved template: the button says Save changes and the discard prompt says the changes are not saved. */
	editing: boolean;
	/** Disabled roles and categories that loaded rows still hold, keyed by row; each is offered on its own row only. */
	heldRoles?: Readonly<Record<string, Option>>;
	heldCategories?: Readonly<Record<string, Option>>;
	commit: CommitTemplate;
};

/** The template form shared by Create Template and Edit Template. */
export const BaselineTemplateForm = ({
	roles,
	categories,
	currency,
	existingNames,
	initialDraft,
	editing,
	heldRoles,
	heldCategories,
	commit,
}: Props): JSX.Element => {
	const {formatMessage} = useIntl();
	const history = useHistory();
	const [draft, setDraft] = useState<TemplateDraft>(initialDraft);
	const [errors, setErrors] = useState<FieldErrors>({});
	const [submitting, setSubmitting] = useState(false);
	const [saved, setSaved] = useState(false);

	const {showPrompt, handleDiscard, closePrompt} = useRouterPrompt({hasChanges: isDirty(draft, initialDraft) && !saved});
	// The handlers change identity as the blocked location changes; the modal must call the latest.
	const promptHandlers = useRef({handleDiscard, closePrompt});
	promptHandlers.current = {handleDiscard, closePrompt};
	const promptOpen = useRef(false);
	promptOpen.current = showPrompt;
	useEffect(() => {
		if (showPrompt) {
			showDiscardTemplateChangesModal({
				onDiscard: () => promptHandlers.current.handleDiscard(),
				onKeepEditing: () => promptHandlers.current.closePrompt(),
				editing,
			});
		}
		// Only a newly shown prompt opens the modal; the handlers are read through the ref.
	}, [showPrompt]);

	// Runs after useRouterPrompt has dropped its blocker for the now-clean state.
	useEffect(() => {
		if (saved) {
			// A discard modal opened while the request was in flight has nothing left to guard.
			if (promptOpen.current) {
				closeDiscardTemplateChangesModal();
			}
			closePrompt();
			history.push(BASELINE_TEMPLATES_PATH);
		}
		// Must fire only when the template has just been saved.
	}, [saved]);

	const fieldError = (path: string): string | undefined => (errors[path] ? formatMessage({id: errors[path]}) : undefined);

	const clearErrors = (shouldClear: (path: string) => boolean) => {
		setErrors(current => {
			const cleared = Object.keys(current).filter(shouldClear);
			if (cleared.length === 0) {
				return current;
			}
			const rest = {...current};
			cleared.forEach(path => delete rest[path]);
			return rest;
		});
	};

	const changeTemplate = (patch: Partial<TemplateDraft>, changedPath?: string) => {
		setDraft(current => ({...current, ...patch}));
		clearErrors(path => path === changedPath);
	};

	// A row edited back to untouched is dropped on save, so none of its errors still apply.
	const changePhase = (phaseKey: string) => (patch: Partial<PhaseDraft>, changedPath?: string) => {
		setDraft(current => updatePhase(current, phaseKey, patch));
		const untouched = untouchedRowKeys(patch);
		clearErrors(path => path === changedPath || untouched.some(key => path.startsWith(`${key}.`)));
	};

	// Removing a phase, a deliverable or a line takes its errors with it, whichever control removed it.
	useEffect(() => {
		setErrors(current => withoutRemovedRowErrors(current, draft));
	}, [draft]);

	const changeDraft = (update: (current: TemplateDraft) => TemplateDraft) => setDraft(current => update(current));

	// Turning deliverables off moves lines out of their cards, so it asks first once any phase has a deliverable.
	const toggleDeliverables = () => {
		if (!draft.includeDeliverables) {
			changeDraft(current => setIncludeDeliverables(current, true));
		} else if (!hasDeliverableContent(draft)) {
			changeDraft(current => setIncludeDeliverables(current, false));
		} else {
			showDisableTemplateDeliverablesModal({
				onConfirm: () => changeDraft(current => setIncludeDeliverables(current, false)),
			});
		}
	};

	const onNameBlur = () => {
		if (isNameTaken(draft.name, existingNames)) {
			setErrors(current => ({...current, name: NAME_TAKEN_ERROR}));
		}
	};

	const onSubmit = () => {
		const found = validate(draft, existingNames);
		setErrors(found);
		const firstInvalid = Object.keys(found)[0];
		if (firstInvalid) {
			scrollToField(firstInvalid);
			return;
		}
		setSubmitting(true);
		commit(
			normalise(draft),
			() => setSaved(true),
			(error, onFailure) => {
				setSubmitting(false);
				if (String(error).includes(`${BASELINE_TEMPLATE_NAME_TAKEN}:`)) {
					setErrors({name: NAME_TAKEN_ERROR});
					scrollToField('name');
					return;
				}
				onFailure?.(error);
			}
		);
	};

	return (
		<FlexRow gap="xl" alignItems="flex-start" data-cy="baseline-template-form">
			<Editor gap="l">
				<TemplateDetailsCard
					draft={draft}
					fieldError={fieldError}
					onChange={changeTemplate}
					onNameBlur={onNameBlur}
					onToggleDeliverables={toggleDeliverables}
				/>
				<FlexRow justifyContent="space-between" alignItems="center">
					<SubHeading>{formatMessage({id: 'common.phases'})}</SubHeading>
					<Button
						emphasis={EMPHASIS.MONO}
						icon="plus"
						onPress={() => setDraft(current => addPhase(current))}
						data-cy="baseline-template-add-phase"
					>
						{formatMessage({id: 'baseline_templates.add_phase'})}
					</Button>
				</FlexRow>
				{draft.phases.map((phase, index) => (
					<PhaseEditor
						key={phase.key}
						phase={phase}
						index={index}
						includeDeliverables={draft.includeDeliverables}
						canDelete={draft.phases.length > 1}
						roles={roles}
						categories={categories}
						heldRoles={heldRoles}
						heldCategories={heldCategories}
						currency={currency}
						fieldError={fieldError}
						onChange={changePhase(phase.key)}
						onDelete={() => setDraft(current => removePhase(current, phase.key))}
						deliverables={draft.deliverables}
						onTemplateChange={changeDraft}
					/>
				))}
			</Editor>
			<TemplateSummaryPanel
				draft={draft}
				submitting={submitting}
				submitLabel={formatMessage({
					id: editing ? 'baseline_templates.save_changes' : 'baseline_templates.create_template',
				})}
				submitCy={editing ? 'baseline-template-save' : 'baseline-template-create'}
				onSubmit={onSubmit}
				onCancel={() => history.push(BASELINE_TEMPLATES_PATH)}
			/>
		</FlexRow>
	);
};
